/**
 * AuthProvider - Authentication State Initializer
 *
 * Initializes auth state on app boot and syncs with Zustand store.
 *
 * Features:
 * - Auto-loads session on mount
 * - Subscribes to auth state changes
 * - Profile caching with stale-while-revalidate
 * - Retry logic with exponential backoff
 * - Proper event handling (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, etc.)
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '~/shared/lib/supabase';
import { useAuthStore } from '~/core/store/useAuthStore';
import { useWorkspaceStore } from '~/core/store/useWorkspaceStore';
import { updateSyncWorkerToken, clearSyncWorkerClient } from '~/core/sync/workerClient';
import { resetSyncInitializer } from '~/core/sync/SyncInitializer';
import { initializeAuthBroker, getAuthEpoch } from '~/core/auth/AuthBroker';
import { loadSubscriptions } from '~/services/user/subscriptionService';
import { useSubscriptionStore } from '~/core/store/useSubscriptionStore';
import { localDb, waitForLocalDb, clearCachedMasterData } from '~/db/client';
import type { UserProfile, AuthErrorType } from '~/core/store/useAuthStore';

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_RETRY_COUNT = 3;
const STALE_TIME = 5 * 60 * 1000; // 5 minutes - refetch if older
const CACHE_EXPIRE_TIME = 24 * 60 * 60 * 1000; // 24 hours - hard expiry
const FETCH_TIMEOUT = 10 * 1000; // 10 seconds - prevent infinite hanging
const IDB_TIMEOUT = 2 * 1000; // 2 seconds - IndexedDB should be fast; if not, skip

// ============================================================================
// TYPES
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

/** Profile query result type */
interface ProfileQueryResult {
  id: string;
  name: string | null;
  role: string | null;  // Deprecated: moved to teacher_academies
  color: string | null; // Deprecated: moved to teacher_academies
  is_app_admin: boolean | null;
}

/** Teacher academy query result with joined academy and role/color */
interface TeacherAcademyQueryResult {
  academy_id: string;
  is_primary: boolean;
  role: string | null;
  color: string | null;
  academies: { id: string; name: string } | null;
}

// ============================================================================
// CACHE HELPERS (IndexedDB)
// ============================================================================

/**
 * Race a promise against a timeout. Unlike withTimeout(), this does NOT
 * check navigator.onLine — IndexedDB is local and works offline.
 * Returns fallback value on timeout instead of throwing.
 */
function withIdbTimeout<T>(promise: Promise<T>, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), IDB_TIMEOUT)),
  ]);
}

/**
 * IndexedDB profile cache — replaces localStorage for offline reliability.
 * Uses the same CacheEntry pattern as ISSUE_051 master data.
 * All operations are timeout-guarded to prevent auth flow blocking.
 */
async function getCachedProfile(userId: string): Promise<{ profile: UserProfile; isStale: boolean } | null> {
  try {
    await waitForLocalDb();
    const entry = await withIdbTimeout(localDb.cachedProfiles.get(userId), undefined);
    if (!entry) return null;

    const age = Date.now() - entry.updatedAt;

    // Hard expiry - don't use if too old
    if (age > CACHE_EXPIRE_TIME) return null;

    // Check if stale (needs background refresh)
    const isStale = age > STALE_TIME;

    return { profile: entry.data as UserProfile, isStale };
  } catch {
    return null;
  }
}

async function setCachedProfile(userId: string, profile: UserProfile): Promise<void> {
  try {
    await waitForLocalDb();
    await withIdbTimeout(
      localDb.cachedProfiles.put({ key: userId, data: profile, updatedAt: Date.now() }),
      undefined as unknown as string,
    );
  } catch {
    // Ignore IndexedDB errors
  }
}

/**
 * 로그아웃·세션만료 시 IndexedDB **캐시** 정리 (cachedProfiles·cachedStudents … `cached*` 전 테이블).
 * 다음 사용자에게 이전 사용자의 마스터데이터가 보이지 않게 한다. 서버에서 다시 받으면 복구됨.
 *
 * 🔴 ISSUE_195 D1: 예전엔 `clearAllLocalData()` 를 불러 **`sessions` 유일본까지** 지웠다.
 * 무료(미결제/만료/구독 미로드) 상태의 세션은 서버에 사본이 없어서 그대로 영구 소실됐고,
 * 이 함수는 명시적 로그아웃뿐 아니라 **토큰 만료·getSession 실패**에서도 호출되므로
 * 사용자가 아무 것도 하지 않아도 데이터가 사라졌다. 세션은 학원(workspaceId) 스코프라
 * 다른 사용자가 로그인해도 조회 경로가 닿지 않으므로, 유지하는 편이 안전하다.
 */
async function clearCachedProfile(): Promise<void> {
  try {
    await waitForLocalDb();
    await clearCachedMasterData();
  } catch {
    // IndexedDB 초기화 전이거나 오류 시 무시
  }
}

// ============================================================================
// NETWORK & TIMEOUT HELPERS
// ============================================================================

class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

class OfflineError extends Error {
  constructor(message = 'Network is offline') {
    super(message);
    this.name = 'OfflineError';
  }
}

/**
 * Check if network is online before making request
 */
function checkOnline(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new OfflineError();
  }
}

/**
 * Wraps a promise or thenable with a timeout and network check
 * @param promiseOrThenable The promise or thenable (like Supabase queries) to wrap
 * @param ms Timeout in milliseconds
 * @returns Promise that rejects with TimeoutError if timeout exceeded
 */
function withTimeout<T>(promiseOrThenable: Promise<T> | PromiseLike<T>, ms: number): Promise<T> {
  // Check network first - fail fast if offline
  checkOnline();

  // Convert thenable to proper Promise
  const promise = Promise.resolve(promiseOrThenable);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(`Request timed out after ${ms}ms`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

// ============================================================================
// RETRY HELPERS
// ============================================================================

/** Calculate delay for exponential backoff */
function getRetryDelay(retryCount: number): number {
  // 1s, 2s, 4s
  return Math.min(1000 * Math.pow(2, retryCount), 4000);
}

/** Determine error type from error object */
function getErrorType(error: unknown): AuthErrorType {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'network';

  // Network-related errors
  if (error instanceof TimeoutError) return 'network';
  if (error instanceof OfflineError) return 'network';

  if (error && typeof error === 'object') {
    const err = error as { code?: string; message?: string; status?: number; name?: string };

    // Timeout/Offline error by name
    if (err.name === 'TimeoutError' || err.name === 'OfflineError' || err.name === 'AbortError') return 'network';

    // Supabase error codes
    if (err.code === 'PGRST116') return 'profile_not_found'; // No rows returned
    if (err.code === '42501' || err.code === '42P01') return 'server'; // Permission/table errors
    if (err.status === 401 || err.status === 403) return 'auth';
    if (err.message?.includes('network') || err.message?.includes('fetch') || err.message?.includes('timeout') || err.message?.includes('abort')) return 'network';
  }

  return 'server';
}

/** Get user-friendly error message */
function getErrorMessage(type: AuthErrorType): string {
  switch (type) {
    case 'network':
      return '네트워크 연결을 확인해주세요';
    case 'auth':
      return '인증이 만료되었습니다. 다시 로그인해주세요';
    case 'profile_not_found':
      return '프로필을 찾을 수 없습니다';
    case 'server':
      return '서버 오류가 발생했습니다';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AuthProvider({ children }: AuthProviderProps) {
  const setSession = useAuthStore((state) => state.setSession);
  const setProfile = useAuthStore((state) => state.setProfile);
  const setProfileLoading = useAuthStore((state) => state.setProfileLoading);
  const setRefreshing = useAuthStore((state) => state.setRefreshing);
  const setAuthError = useAuthStore((state) => state.setAuthError);
  const clearError = useAuthStore((state) => state.clearError);
  const setLastProfileFetch = useAuthStore((state) => state.setLastProfileFetch);
  const signOutFromStore = useAuthStore((state) => state.signOut);
  const setJustLoggedIn = useAuthStore((state) => state.setJustLoggedIn);

  // Workspace actions
  const initializeWorkspace = useWorkspaceStore((state) => state.initializeFromContext);
  const clearWorkspace = useWorkspaceStore((state) => state.clearWorkspace);

  // Prevent StrictMode double-initialization
  const initStartedRef = useRef(false);

  useEffect(() => {
    const alreadyInitialized = useAuthStore.getState().isInitialized;

    // StrictMode guard: prevent double-init before async completes
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    // If Supabase is not configured, just mark as initialized
    if (!isSupabaseConfigured() || !supabase) {
      if (!alreadyInitialized) {
        setSession(null);
      }
      return;
    }

    const sb = supabase;

    // ISSUE_047: Initialize AuthBroker for centralized token management
    initializeAuthBroker();

    /** Load subscription data after workspace is initialized */
    const loadSubs = (_profileId: string) => {
      const ws = useWorkspaceStore.getState().active;
      void loadSubscriptions(ws.type === 'academy' ? ws.id : null);
    };

    /**
     * Fetch profile with retry logic
     */
    const fetchProfileWithRetry = async (
      userId: string,
      retryCount = 0
    ): Promise<UserProfile | null> => {
      // Auth dead 가드
      if (useAuthStore.getState().authLiveness === 'dead') return null;

      // Don't even attempt if offline — use cached profile silently
      if (!navigator.onLine) {
        const cached = await getCachedProfile(userId);
        return cached?.profile ?? null;
      }

      try {
        // Fetch profile by auth_user_id (profile.id is different from auth.user.id)
        // Wrap with timeout to prevent infinite hanging
        const profileResult = await withTimeout(
          sb
            .from('profiles')
            .select('id, name, role, color, is_app_admin')
            .eq('auth_user_id', userId)
            .single(),
          FETCH_TIMEOUT
        );
        const profileData = profileResult.data as ProfileQueryResult | null;
        const profileError = profileResult.error;

        if (profileError) {
          throw profileError;
        }

        if (!profileData) {
          throw { code: 'PGRST116', message: 'Profile not found' };
        }

        // Fetch primary academy via teacher_academies (use profile.id, not auth.user.id)
        // Role and color are now stored in teacher_academies, not profiles
        const academyResult = await withTimeout(
          sb
            .from('teacher_academies')
            .select('academy_id, is_primary, role, color, academies(id, name)')
            .eq('teacher_id', profileData.id)
            .eq('is_primary', true)
            .maybeSingle(),
          FETCH_TIMEOUT
        );
        const academyData = academyResult.data as TeacherAcademyQueryResult | null;

        // Prefer role/color from teacher_academies, fallback to profiles for migration compatibility
        const role = (academyData?.role || profileData.role)?.toLowerCase() as 'owner' | 'admin' | 'teacher' | undefined;
        const color = academyData?.color || profileData.color;

        const result: UserProfile = {
          id: profileData.id,
          name: profileData.name || 'Unknown',
          role: role || 'teacher',
          ...(color && { color }),
          ...(academyData?.academies && {
            academyId: academyData.academies.id,
            academyName: academyData.academies.name,
          }),
          ...(profileData.is_app_admin && { isAppAdmin: true }),
        };

        // Cache the profile to IndexedDB
        void setCachedProfile(userId, result);
        setLastProfileFetch(Date.now());
        clearError();

        return result;
      } catch (err) {
        const errorType = getErrorType(err);

        // Don't retry for auth errors
        if (errorType === 'auth') {
          setAuthError({
            type: errorType,
            message: getErrorMessage(errorType),
            retryCount,
          });
          return null;
        }

        // Profile not found — auto-create for authenticated users
        // Handles trigger failure (e.g., Kakao OAuth without email)
        if (errorType === 'profile_not_found') {
          if (retryCount === 0) {
            try {
              const { data: { user: authUser } } = await sb.auth.getUser();
              if (authUser) {
                const meta = authUser.user_metadata;
                const email = authUser.email
                  || meta?.['email'] as string | undefined
                  || `${authUser.id}@no-email.noten.app`;
                const name = (meta?.['name'] as string | undefined)
                  || (meta?.['full_name'] as string | undefined)
                  || (meta?.['nickname'] as string | undefined)
                  || email.split('@')[0]
                  || 'Unknown';

                const { error: upsertError } = await sb
                  .from('profiles')
                  .upsert({
                    id: authUser.id,
                    auth_user_id: authUser.id,
                    email,
                    name,
                  }, { onConflict: 'id' });

                if (!upsertError) {
                  // Profile created, retry fetch
                  return fetchProfileWithRetry(userId, 1);
                }
              }
            } catch {
              // Auto-creation failed, fall through to error
            }
          }

          setAuthError({
            type: errorType,
            message: getErrorMessage(errorType),
            retryCount,
          });
          return null;
        }

        // Network errors — return cached profile immediately, don't retry aggressively
        if (errorType === 'network') {
          const cached = await getCachedProfile(userId);
          if (cached) return cached.profile;
          // No cache and no network — report error
          setAuthError({ type: errorType, message: getErrorMessage(errorType), retryCount });
          return null;
        }

        // Server errors — retry with backoff
        if (retryCount < MAX_RETRY_COUNT) {
          const delay = getRetryDelay(retryCount);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return fetchProfileWithRetry(userId, retryCount + 1);
        }

        // Max retries exceeded
        setAuthError({
          type: errorType,
          message: getErrorMessage(errorType),
          retryCount,
        });
        return null;
      }
    };

    /**
     * Initialize auth on mount
     */
    const initializeAuth = async () => {
      try {
        // ISSUE_063: getSession()은 로컬 작업 — withTimeout/checkOnline 불필요
        let session = null;
        try {
          const result = await sb.auth.getSession();
          session = result.data.session;
          if (result.error) throw result.error;
        } catch (e) {
          const persistedProfile = useAuthStore.getState().profile;
          const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

          if (persistedProfile && isOffline) {
            // ISSUE_063: 오프라인에서 persisted profile로 진입 허용
            useAuthStore.setState({ isInitialized: true });
            initializeWorkspace(persistedProfile.id);
            loadSubs(persistedProfile.id);
            return;
          }

          // 온라인인데 getSession 실패 — 세션 만료 또는 스토리지 손상
          // stale profile을 정리하여 ghost login 방지
          if (persistedProfile) {
            void clearCachedProfile();
            clearWorkspace();
            signOutFromStore();
            useAuthStore.setState({ isInitialized: true });
          } else {
            setSession(null);
          }
          return;
        }

        setSession(session);

        if (!session?.user) {
          // 세션 없음 — 온라인이면 세션 만료, stale profile 정리
          const staleProfile = useAuthStore.getState().profile;
          if (staleProfile && navigator.onLine) {
            void clearCachedProfile();
            clearWorkspace();
            signOutFromStore();
            useAuthStore.setState({ isInitialized: true });
          }
          // 오프라인이면 profile 유지 (ISSUE_063)
          return;
        }

        if (session?.user) {
          const userId = session.user.id;

          // 1. Check if profile already exists in store (from persist)
          const persistedProfile = useAuthStore.getState().profile;
          if (persistedProfile && persistedProfile.id === userId) {
            // ALWAYS initialize workspace - this refreshes academies from server
            // and validates that the cached active workspace still exists
            initializeWorkspace(persistedProfile.id);
            loadSubs(persistedProfile.id);

            // Profile already in store, just check if stale AND online
            const lastFetch = useAuthStore.getState().lastProfileFetch;
            const isStale = !lastFetch || (Date.now() - lastFetch > STALE_TIME);
            if (isStale && navigator.onLine) {
              setRefreshing(true);
              fetchProfileWithRetry(userId).then((fresh) => {
                if (fresh) setProfile(fresh);
                setRefreshing(false);
              }).catch(() => {
                setRefreshing(false);
              });
            }
            return;
          }

          // 2. Check IndexedDB cache (for stale-while-revalidate)
          const cached = await getCachedProfile(userId);

          if (cached) {
            // Use cached profile immediately
            setProfile(cached.profile);

            // Initialize workspace
            initializeWorkspace(cached.profile.id);
            loadSubs(cached.profile.id);

            if (cached.isStale && navigator.onLine) {
              // Stale AND online - refresh in background
              setRefreshing(true);
              fetchProfileWithRetry(userId).then((fresh) => {
                if (fresh) setProfile(fresh);
                setRefreshing(false);
              }).catch(() => {
                setRefreshing(false);
              });
            }
          } else {
            // No cache - show loading and fetch
            setProfileLoading(true);

            const profile = await fetchProfileWithRetry(userId);
            if (profile) {
              setProfile(profile);

              // Initialize workspace after profile is loaded
              initializeWorkspace(profile.id);
              loadSubs(profile.id);
            }
            setProfileLoading(false);
          }
        }
      } catch {
        setSession(null);
        setProfileLoading(false);
      }
    };

    // Initialize on mount (skip if already initialized)
    if (!alreadyInitialized) {
      initializeAuth();
    }

    // Subscribe to auth state changes (ALWAYS set up, even after StrictMode remount)
    const { data: { subscription } } = sb.auth.onAuthStateChange(
      async (event, session) => {
        switch (event) {
          case 'INITIAL_SESSION':
            // Already handled by initializeAuth
            break;

          case 'SIGNED_IN':
            // Only process if this is actually a new sign-in, not a duplicate event
            const currentProfile = useAuthStore.getState().profile;
            if (currentProfile && session?.user?.id === currentProfile.id) {
              setSession(session);
              break;
            }

            setSession(session);
            clearError();

            // ISSUE_047: Update sync worker with new access token and epoch
            if (session?.access_token) {
              updateSyncWorkerToken(session.access_token, getAuthEpoch());
            }

            if (session?.user) {
              try {
                // Check IndexedDB cache first before showing loading
                const cached = await getCachedProfile(session.user.id);
                if (cached) {
                  setProfile(cached.profile);
                  // Initialize workspace
                  initializeWorkspace(cached.profile.id);
                  loadSubs(cached.profile.id);
                  // Don't set justLoggedIn - this is a session restore, not fresh login
                  if (cached.isStale && navigator.onLine) {
                    // Background refresh only when online
                    setRefreshing(true);
                    fetchProfileWithRetry(session.user.id).then((fresh) => {
                      if (fresh) setProfile(fresh);
                      setRefreshing(false);
                    }).catch(() => {
                      setRefreshing(false);
                    });
                  }
                } else {
                  // No cache = fresh login, show animation
                  setJustLoggedIn(true);
                  setProfileLoading(true);
                  const profile = await fetchProfileWithRetry(session.user.id);
                  if (profile) {
                    setProfile(profile);
                    // Initialize workspace
                    initializeWorkspace(profile.id);
                    loadSubs(profile.id);
                  }
                  setProfileLoading(false);
                }

                // Check for pending invite token
                try {
                  const pendingInviteToken = sessionStorage.getItem('pending_invite_token');
                  if (pendingInviteToken) {
                    sessionStorage.removeItem('pending_invite_token');
                    // Validate token format (alphanumeric, hyphens, underscores only)
                    const isValidToken = /^[a-zA-Z0-9_-]+$/.test(pendingInviteToken);
                    if (isValidToken) {
                      const url = new URL('/invite', window.location.origin);
                      url.searchParams.set('token', pendingInviteToken);
                      window.location.href = url.toString();
                    }
                  }
                } catch {
                  // Ignore sessionStorage errors
                }
              } catch {
                // Ensure loading states are reset on any error
                setProfileLoading(false);
                setRefreshing(false);
              }
            }
            break;

          case 'SIGNED_OUT':
            void clearCachedProfile();
            clearWorkspace();
            useSubscriptionStore.getState().clear();
            signOutFromStore();
            // Clean up sync worker to prevent data leakage between users
            void clearSyncWorkerClient();
            // Reset sync initializer flag to allow re-initialization on next login
            resetSyncInitializer();
            break;

          case 'TOKEN_REFRESHED':
            // Auth dead면 세션 업데이트 스킵 — forceLogout 진행 중
            if (useAuthStore.getState().authLiveness === 'dead') break;
            // Update session and sync worker token
            // ISSUE_047: AuthBroker handles this via its own onAuthStateChange listener,
            // but we still update here for backwards compatibility
            if (session) {
              setSession(session);
              // Update sync worker with new access token and epoch
              if (session.access_token) {
                updateSyncWorkerToken(session.access_token, getAuthEpoch());
              }
            }
            break;

          case 'USER_UPDATED':
            // User metadata changed, refetch profile
            setSession(session);
            if (session?.user) {
              try {
                setRefreshing(true);
                const profile = await fetchProfileWithRetry(session.user.id);
                if (profile) {
                  setProfile(profile);
                }
              } catch {
                // Ignore fetch errors
              } finally {
                setRefreshing(false);
              }
            }
            break;

          default:
            // Unknown events - update session, don't refetch profile
            if (session) {
              setSession(session);
            }
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Note: Empty deps - Zustand actions are stable refs, and we only want to run once on mount

  return <>{children}</>;
}

export default AuthProvider;
